import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';

@customElement('voy-timeinput')
export class VoyTimeinput extends LitElement
{
    constructor() {
        super();

        this._onInput = this._onInput.bind(this);
    }

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
                display: flex;
                flex-direction: column;
            }

            label {
                font-family: MaerskText;
                font-size: 14px;
                margin-bottom: 8px;
                color: var(--functional-grey-800);
            }

            label[readonly] {
                pointer-events: none;
                color: var(--functional-grey-800);
            }
            
            input {
                font-family: MaerskText;
                font-size: 14px;
                box-sizing: border-box;
                padding-left: 10px;
                padding-right: 10px;
                outline: none;
                color: var(--neutrals-grey-02);
                height: 52px;
                border: 1px solid var(--neutrals-grey-06);
                background-color: var(--neutrals-grey-10, white);
                border-radius: 3px;
            }

            input[readonly] {
                pointer-events: none;
                color: var(--neutrals-grey-04);
                background: var(--neutrals-grey-08);
            }

            .input:focus {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
            }

            input[invalid] {
                border: 1px solid var(--feedback-danger, red);
            }

            .utcWarn {
                font-weight: bold;
                color: var(--warning-yellow-7);
            }
            `
        ];
    }

    @property({type: String}) label: string = "";
    @property({type: Boolean}) readonly: boolean = false;
    @property({type: Boolean, attribute: "allow-empty"}) allowEmpty: boolean = false;
    @property({type: Boolean, attribute: "invalid"}) invalid: boolean = false;
    @property({type: String, attribute: "terminal-id"}) terminalId: string = "";
    @property({type: String, attribute: "custom-label"}) customLabel: string = "";
    @property({type: Boolean, attribute: "warn-utc"}) warnUTC: boolean = false;
    @property({type: Boolean, attribute: "use-blur-event"}) useBlurEvent: boolean = false;

    get value()
    {
        return this._value;
    }

    set value(newValue: number)
    {
        if (newValue != this._value)
        {
            this._value = newValue;
            this._updateInputValue();    
        }
    }

    render()
    {
        const isUTC = (this.terminalId === "" || TerminalsModel!.isPseudoTerminal(this.terminalId));

        return html`
        <label for="input" ?readonly=${this.readonly}>
            ${this.customLabel === "" ? html`${this.label} <span class=${isUTC && this.warnUTC ? "utcWarn" : ""}>${isUTC ? "(UTC)" : "(local time)"}</span>` :
            this.customLabel}
        </label>
        <input id="input" type="datetime-local" ?invalid=${this.invalid} ?required=${!this.allowEmpty} .value=${this._inputValue} ?readonly=${this.readonly}>
        `;
    }

    firstUpdated() {
        // Option to listen for date changes through input or blur
        // Use blur when input event should only fire when user leaves the input field i.e. have finished typing a date
        this._input = this.shadowRoot!.querySelector("#input");
        this._input!.addEventListener(this.useBlurEvent ? "blur" : "input", this._onInput)
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._input!.removeEventListener(this.useBlurEvent ? "blur" : "input", this._onInput)
    }

    updated(changedValues: PropertyValues)
    {
        if (changedValues.has("terminalId"))
        {
            this._updateInputValue();
        }
    }

    @property({ attribute: false }) _inputValue: string = "";
    private _value: number = -1;
    private _input: HTMLInputElement|null = null;

    private async _onInput(evt: Event): Promise<void>
    {
        const inputValue = (<HTMLInputElement>evt.target).value;

        // Stop the real input event from propagating (since we may need to wait for a timezone conversion)
        evt.stopPropagation();

        // Guard against the full datetime not being input yet
        if (!Date.parse(inputValue))
        {
            this._value = -1;
            this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
            return;
        }

        // The datetime-local component should not include seconds, so normalize to an ISO-like string (without time zone, as it is unknown as of yet)
        const parsedDateString = inputValue.substring(0, 16) + ':00';

        if (this.terminalId && !TerminalsModel!.isPseudoTerminal(this.terminalId))
        {
            this._value = new Date(await TerminalsModel!.getUTCFromTerminalLocaltime(this.terminalId, parsedDateString)).getTime();
        }
        else
        {
            // Use UTC when no terminal or pseudo terminal has been set
            this._value = new Date(parsedDateString + "Z").getTime();
        }

        this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    }

    private async _updateInputValue(): Promise<void>
    {
        if (this._value < 0)
        {
            this._inputValue = "";
            return;
        }

        let timeString = new Date(this._value).toISOString();

        if (this.terminalId && !TerminalsModel!.isPseudoTerminal(this.terminalId))
        {
            const valueCopy = this._value;
            const terminalId = this.terminalId;
            timeString = await TerminalsModel!.getTerminalLocaltime(this.terminalId, timeString);
            if (this._value !== valueCopy || this.terminalId !== terminalId)
            {
                // Value changed while we were converting to local time, bail out
                return;
            }
        }

        // The timestamp is returned with full timezone information (e.g. 2019-07-01T12:00:00.000+01:00),
        // so trim off everything except the date, hours and minutes
        this._inputValue = timeString.substring(0, 16);
    };
};
